import { Dispatch } from 'redux';
import * as api from '../utils/api';
import { NavigateFunction } from 'react-router-dom';

import { notifyError, notifySuccess } from '../utils/functions';
import { showOverlay } from './overlay';
import { cloneDeep } from 'lodash';

interface ModulesState {
  list?: IModule[];
  current?: IModule;
  authorModules?: IModule[];
}

const actions = {
  CLEAR_MODULE: 'lms-ui/modules/CLEAR_MODULE',
  UPDATE: 'lms-ui/modules/UPDATE',
  GET_MODULES: 'lms-ui/modules/GET_MODULES',
  GET_MODULE: 'lms-ui/modules/GET_MODULE',
};

interface ModulesAction {
  type: typeof actions.GET_MODULES | typeof actions.GET_MODULE | typeof actions.UPDATE;
  payload: IModule | IModule[];
}

type ModulesActionTypes = ModulesAction;

const initialState: ModulesState = {
  list: [],
  current: undefined,
};

export const update = (payload: ModulesState) => {
  return {
    type: actions.UPDATE,
    payload,
  };
};

export const clearModule = () => {
  return {
    type: actions.CLEAR_MODULE,
  };
};

export const getModule =
  ({ courseId, moduleId }: { courseId: string; moduleId: string }) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await api.getModule({ courseId, moduleId });
      const current = response.data as IModule;
      dispatch({ type: actions.GET_MODULE, payload: current });
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

const getFormData = (data: IModule) => {
  const formData = new FormData();
  const props = [
    'title',
    'tooltipText',
    'instructions',
    'published',
    'type',
    'isOptional',
    'disableRetake',
    'takeAnytime',
    'timeReleasePeriod',
    'authorId',
    'videoFile',
    'video',
    'pdfFile',
    'wordFile',
    'document',
    'embedded',
    'categoryId',
  ];

  props.forEach((p) => {
    const value = data[p];
    if (value) {
      switch (p) {
        case 'image':
        case 'videoFile':
        case 'pdfFile':
        case 'wordFile':
          formData.append(p, value);
          break;
        case 'document':
        case 'video':
        case 'embedded':
          Object.keys(value).forEach((k) => {
            formData.append(`${p}[${k}]`, value[k]);
          });
          break;
        default:
          formData.append(p, value.toString());
          break;
      }
    }
  });

  return formData;
};

export const addModule =
  ({
    courseId,
    module,
    navigate,
  }: {
    courseId: string;
    module: IModule;
    navigate: NavigateFunction;
  }) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(showOverlay(true));
      const formData = getFormData(module);
      const response = await api.addModule({ courseId, formData });
      const current = response.data as IModule;
      dispatch(update({ current }));
      navigate(`/admin/courses/${courseId}/modules/${current._id}`);
      dispatch(showOverlay());
      notifySuccess('Module added');
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const updateModule =
  ({ courseId, module }: { courseId: string; module: IModule }) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(showOverlay(true));
      const formData = getFormData(module);
      const response = await api.updateModule({
        courseId,
        moduleId: module._id as string,
        formData,
      });
      const current = response.data as IModule;
      dispatch(update({ current }));
      dispatch(showOverlay());
      notifySuccess('Module updated');
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const queueTranscription =
  ({ courseId, module }: { courseId: string; module: IModule }) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(showOverlay(true));
      const response = await api.queueTranscription(courseId, module._id as string);
      const current = response.data as IModule;
      const updated = cloneDeep(module);
      if (updated.video && current.video) {
        updated.video.captionsQueued = current.video.captionsQueued;
        updated.video.captions = current.video.captions;
      }

      dispatch(update({ current: updated }));
      dispatch(showOverlay());
      notifySuccess('Transcription queued');
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const deleteModule =
  ({
    courseId,
    moduleId,
    navigate,
  }: {
    courseId: string;
    moduleId: string;
    navigate: NavigateFunction;
  }) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(showOverlay(true));
      await api.deleteModule({ courseId, moduleId });
      dispatch(update({ current: undefined }));
      navigate(`/admin/courses/${courseId}`);
      dispatch(showOverlay());
      notifySuccess('Module deleted');
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const getAuthorModules = () => async (dispatch: Dispatch) => {
  try {
    const response = await api.getAuthorModules();
    dispatch(update({ authorModules: response.data }));
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export default function reducer(state = initialState, action: ModulesActionTypes): ModulesState {
  switch (action.type) {
    case actions.UPDATE: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case actions.GET_MODULES: {
      return {
        ...state,
        list: action.payload as IModule[],
      };
    }
    case actions.GET_MODULE: {
      const module = action.payload as IModule;
      return {
        ...state,
        current: module,
      };
    }
    case actions.CLEAR_MODULE: {
      return {
        ...state,
        current: undefined,
      };
    }
    default:
      return state;
  }
}
